import {
  Badge,
  Button,
  Input,
  Typography,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { Trash2 } from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import {
  FIELD_CONFIG,
  type LayoutField,
} from "./SignatureRequestPdfLayoutEditor.constants";

type SignatureRequestPdfLayoutEditorDetailsProps = {
  isDraggingField: boolean;
  onRemoveField: (fieldId: string) => void;
  onUpdateField: (
    fieldId: string,
    updates: Partial<
      Pick<
        LayoutField,
        | "height"
        | "label"
        | "signerId"
        | "width"
        | "x"
        | "y"
        | "fontFamily"
        | "fontSize"
      >
    >,
  ) => void;
  selectedField: LayoutField | null;
};

type SignatureRequestPdfLayoutEditorSignerFieldsProps = {
  fields: LayoutField[];
  activeSignerId: string;
  fieldDetails?: ReactNode;
  onSelectFieldId: (fieldId: string) => void;
  selectedField: LayoutField | null;
};

export function SignatureRequestPdfLayoutEditorSignerFields({
  fields,
  activeSignerId,
  fieldDetails,
  onSelectFieldId,
  selectedField,
}: SignatureRequestPdfLayoutEditorSignerFieldsProps) {
  const visibleFields = fields.filter(
    (field) => field.signerId === activeSignerId,
  );

  return (
    <div className="border-border/70 mt-3 space-y-2 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <Typography className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Existing fields
        </Typography>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
          {visibleFields.length}
        </Badge>
      </div>
      {visibleFields.length ? (
        <div className="space-y-2">
          {visibleFields.map((field) => {
            const fieldLabel = field.label || FIELD_CONFIG[field.type].label;
            const isSelected = field.id === selectedField?.id;

            return (
              <div
                key={field.id}
                className={cn(
                  "border-border overflow-hidden rounded-lg border transition-colors",
                  isSelected
                    ? "border-ring bg-accent text-accent-foreground"
                    : "bg-card",
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelectFieldId(field.id);
                  }}
                  className="hover:bg-accent flex w-full min-w-0 items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors"
                >
                  <span className="min-w-0 truncate">{fieldLabel}</span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    Page {field.page}
                  </span>
                </button>
                {isSelected ? (
                  <div className="border-border/70 border-t pt-3">
                    {fieldDetails}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border-border text-muted-foreground rounded-lg border border-dashed p-3 text-sm">
          No fields assigned yet.
        </div>
      )}
    </div>
  );
}

export function SignatureRequestPdfLayoutEditorDetails({
  isDraggingField,
  onRemoveField,
  onUpdateField,
  selectedField,
}: SignatureRequestPdfLayoutEditorDetailsProps) {
  const getDisplayPercentValue = (value: number) =>
    Math.round(Number(value) * 100);
  const getStoredPercentValue = (value: string) =>
    Math.min(Math.max((Number(value) || 0) / 100, 0.02), 1);

  const handleDeleteDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const fieldId = event.dataTransfer.getData("application/x-field-id");
    if (!fieldId) {
      return;
    }
    onRemoveField(fieldId);
  };

  return (
    <div className="space-y-3 px-3 pb-3">
      {selectedField ? (
        <>
          <label className="block space-y-1">
            <span className="text-muted-foreground text-xs font-medium">
              Label
            </span>
            <Input
              value={selectedField.label}
              onChange={(event) => {
                onUpdateField(selectedField.id, {
                  label: event.target.value,
                });
              }}
            />
          </label>

          {(selectedField.type === "text" || selectedField.type === "date") && (
            <div className="space-y-2">
              <label className="block space-y-1">
                <span className="text-muted-foreground text-xs font-medium">
                  Font
                </span>
                <Select
                  value={selectedField.fontFamily ?? "Times New Roman"}
                  onValueChange={(value) => {
                    onUpdateField(selectedField.id, {
                      fontFamily: value as LayoutField["fontFamily"],
                    });
                  }}
                >
                  <SelectTrigger aria-label="Signer text font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["Times New Roman", "Arial", "Courier New"] as const).map(
                      (family) => (
                        <SelectItem key={family} value={family}>
                          {family}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </label>
              <label className="block space-y-1">
                <span className="text-muted-foreground text-xs font-medium">
                  Font size (pt)
                </span>
                <Input
                  type="number"
                  min={6}
                  max={72}
                  step={0.5}
                  value={selectedField.fontSize ?? 11}
                  onChange={(event) => {
                    onUpdateField(selectedField.id, {
                      fontSize: Math.min(
                        72,
                        Math.max(6, Number(event.target.value) || 11),
                      ),
                    });
                  }}
                />
              </label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1">
              <span className="text-muted-foreground text-xs font-medium">
                Width
              </span>
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                value={getDisplayPercentValue(selectedField.width)}
                onChange={(event) => {
                  onUpdateField(selectedField.id, {
                    width: getStoredPercentValue(event.target.value),
                  });
                }}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-muted-foreground text-xs font-medium">
                Height
              </span>
              <Input
                type="number"
                min={1}
                max={100}
                step={1}
                value={getDisplayPercentValue(selectedField.height)}
                onChange={(event) => {
                  onUpdateField(selectedField.id, {
                    height: getStoredPercentValue(event.target.value),
                  });
                }}
              />
            </label>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive w-full justify-start"
            onClick={() => {
              onRemoveField(selectedField.id);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete field
          </Button>
        </>
      ) : null}

      {isDraggingField ? (
        <div
          className={cn(
            "border-destructive/50 bg-destructive/15 text-destructive hover:bg-destructive/25 flex min-h-24 items-center justify-center rounded-xl border border-dashed px-4 text-center text-sm transition-colors",
          )}
          onDragOver={(event) => {
            event.preventDefault();
          }}
          onDrop={handleDeleteDrop}
        >
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4" />
            <span>Drop a field here to delete it</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
